import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import BakeasyHeaderLabel from '../../components/labels/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import { t } from 'i18next';
import { BakeasyChip } from '../../components/common/BakeasyChip';
import { useNavigate } from 'react-router-dom';
import * as Button from '../../components/buttons/BakeasyButtons';
import {
  clearProductState,
  downloadProducts,
  fetchProducts,
  productsState,
} from '../../redux_store/slices/productsSlice';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { productTableHeaders } from '../../constants/TableHeaders';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { deleteKeyFromObject, preparePayload } from '../../utils/commonUtils';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const Products: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();

  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);

  const [search, setSearch] = useState<string>('');
  const [productsList, setProductsList] = useState<any[]>([]);
  const [count, setCount] = React.useState<number>(0);
  const [tFilter, setTFilter] = React.useState<any>({});

  let { value, rowsPerPage, pageNo, filter } = useAppSelector(productsState);

  function openFilterProductConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'product-filter',
      open: true,
      title: t('PRODUCT_FILTER'),
      data: filter,
      onNoClick: (event: any) => {},
      onYesClick: async (event: any, param: any) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      },
    });
  }

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  const refreshPageData = async (
    newPage: number,
    search?: string,
    filter?: {} | undefined
  ) => {
    let payload = await preparePayload(
      {
        limit: rowsPerPage,
        page: newPage + 1,
        sortBy: 'createdDate',
        sortDirection: -1,
      },
      filter,
      search,
      newPage
    );
    await dispatch(fetchProducts(payload));
    // await fetchData(fetchProducts, payload);
  };

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((product: any) => {
        return {
          _id: product?.productId,
          userProductId: product?.userProductId,
          productName: product?.productName,
          onProductNameClick: () => {
            dispatch(clearProductState());
            navigate('/productDetails', {
              state: { isEdit: false, isView: true, _id: product?.productId },
            });
          },
          storeCost:
            product.storeCost || product.storeCost === 0
              ? parseFloat(product.storeCost)?.toFixed(2)
              : parseFloat('0')?.toFixed(2),
          categoryName: product?.categoryName,
          subCategoryName: product?.subCategoryName,
          status: (
            <BakeasyChip
              label={
                product?.status === 'Active'
                  ? t('Active')
                  : product?.status === 'Inactive'
                    ? t('Inactive')
                    : t('Unavailable')
              }
            />
          ),
          description: product?.description,
        };
      });

      setProductsList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function productsUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={t('PRODUCTS')} />

          <Grid
            container
            spacing={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (
                    event?.target?.value?.length === 0 ||
                    event?.target?.value?.length > 2
                  ) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0
                        ? undefined
                        : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(
                  event: React.KeyboardEvent<HTMLInputElement>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={9} lg={6} xl={6}>
              <div
                className="tableOptions"
                style={{ display: 'flex', justifyContent: 'end' }}
              >
                <Button.Print
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    dispatch(
                      downloadProducts({ ...filter, downloadType: 'pdf' })
                    );
                  }}
                />
                <Button.Export
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    dispatch(
                      downloadProducts({ ...filter, downloadType: 'csv' })
                    );
                  }}
                />
                <Button.Filter
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    openFilterProductConfirmDialog();
                  }}
                />
                <Button.CreateOrder
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    navigate('/orders');
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />

          <Grid>
            {productsList?.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={productTableHeaders}
                tableData={productsList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return productsUI();
};
